import { FC, ChangeEvent, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { generateUuid } from '@lib/uuid';

export enum UiTextAreaSize {
  SMALL = 'px-2 pb-1.5 pt-1.5 h-16',
  DEFAULT = 'px-2 pb-3 pt-3 h-32',
  LARGE = 'px-3 pb-4 pt-4 h-64',
}

interface UiTextAreaProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  id?: string;
  label?: string;
  name?: string;
  placeholder?: string;
  inputRef?: React.Ref<HTMLTextAreaElement> | null;
  onBlur?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  size?: UiTextAreaSize;
  maxLength?: number;
  className?: string;
  labelClassName?: string;
}

export const UiTextArea: FC<UiTextAreaProps> = ({
  id: propsId,
  value,
  name,
  label,
  placeholder = label,
  inputRef = null,
  error,
  errorText,
  disabled,
  onChange,
  onBlur,
  size = UiTextAreaSize.DEFAULT,
  maxLength,
  className = '',
  labelClassName = '',
}) => {
  const id = useMemo(() => {
    return propsId != null ? propsId : generateUuid();
  }, [propsId]);

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => setInputValue(value), [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();
    setInputValue(e.target.value);
    onChange(e);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className={classNames(labelClassName, { 'text-cyan-900': isFocused && !error, 'text-red-500': error })}
        >
          {label}
        </label>
      )}
      <div
        className={classNames('flex relative mb-2', {
          'border-gray-300': isHovered && !isFocused,
          'border-cyan-900': isFocused && !error,
          'bg-white': !disabled && !error,
          'bg-gray-100': disabled,
        })}
      >
        <textarea
          id={id}
          value={inputValue}
          name={name}
          placeholder={placeholder}
          maxLength={maxLength}
          ref={inputRef}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={classNames(
            'block w-full max-h-64 text-sm border border-1 border-solid appearance-none focus:outline-none focus:ring-0 peer',
            {
              'rounded-lg': true,
              [size]: true,
              'border-red-400 focus:border-red-500 text-red-500': error,
              'border-gray-200 focus:border-cyan-900 text-gray-700': !error,
              'hover:border-gray-300': !error && !disabled,
            }
          )}
        />
      </div>
      {error && (
        <p id={`${id}_error`} className="mt-2 text-xs text-red-500">
          <span className="font-medium">{errorText}</span>
        </p>
      )}
    </div>
  );
};
